import React, { useState, useMemo, useEffect, useCallback } from 'react';
import moment from "moment";
import { useListCustomersTopLevel, useListCustomerProfiles, AcceptedCommodity, acceptedCommodityTypes, RiskCropProfile, RiskCropProfileHistory, RiskManagementCustomerProfile } from '../../lib/api/risk/profiles';
import { custom, z } from 'zod';
import { Decimal } from 'decimal.js';
import {
  CashSalesFiltersValidation, RiskManagementCashSales, useListRiskCashSales, useListStagedCashSales,
  useProcessStagedRow, useDeleteStagedRow
} from '../../lib/api/risk/cashSales';
import { CommonDestinationTimezone, GenerateLoadingOrError, genFlatOptionList, getUniqueStrings } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';
import { BearDecimalInput, BearIntInput } from '@/lib/form/ui';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useDebounce from '../otc/useDebounce';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '@/styles/riskCompactTable.css';
import { Badge } from "@/components/ui/badge";
import { X, ChevronDown, ChevronUp, DeleteIcon, Trash2Icon, Columns3 } from "lucide-react";
import { DataTable } from "@/components/ui/virtualizedTable";
import { ColumnDef } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as Collapsible from "@radix-ui/react-collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronRight, Search, Plus, Edit } from "lucide-react";
import { CreateCropProfileDialog, EditCropProfileDialog } from './CropProfileForm';
import { useListCustomers } from '@/lib/api/customer';
import { cn } from '@/lib/utils';
import { formatCommodityToUom, formatNumberToDisplay, safeParseDecimal, sortOptionMonths } from '@/lib/api/common';
import { Separator } from '@/components/ui/separator';
import { Commodities, CommodityOptions, Uom } from '@/lib/api/commodity';
import { RiskProfileDetailModal } from '@/components/modals/RiskProfileDetailModal';
import { CropProfileReportModal } from '@/section/risk/GenerateProfileReportModal';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import SummaryCard from './SummaryCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import RiskManagementSummaryTables from './SummaryTables';
import { useListTraders } from '@/lib/api/trader';
import { Trader } from '@/lib/api/reports';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ContractType, generateContractTypesWith } from '@/lib/api/generalContract';
import { UploadCashReportDialog } from './UploadCashReportDialog';
import { DatePicker } from '@/components/ui/typeable_date_picker';
import { useToast } from "@/components/ui/use-toast";
import { Label } from '@/components/ui/label';
import { BinaryCropTypeSelection, useListRiskSummary, RiskManagementSummaryResult, useListRiskSummaryFullRows, BinaryCropTypeSelectionOptions } from '@/lib/api/risk/summary';
import { createArrayOfSize } from '@/lib/freightReport/frieghtReportFunctions';
import { ContractTypeOptions as OtcContractTypeOptions, ContractType as OtcContractType } from '@/lib/api/otc';
import { RiskCustomerModal } from '@/components/modals/RiskCustomerModal';

type ExpandedState = Record<string, boolean>;

interface TableRow {
  id: string;
  commodity: string;
  commodityId: string;
  customerId: string;
  customerName: string;
  hta: Decimal | null;
  unpxdBasis: Decimal | null;
}

interface OtcTableRow {
  id: string;
  commodity: string;
  commodityId: string;
  customerId: string;
  customerName: string;
  otcId: string;
  otcContract: any;
}

interface CommoditySummaryRow {
  id: string;
  crop_year: number;
  commodity: string;
  commodityId: string;
  customer_id: string;
  customer_name: string;
  acreage: string | Decimal;
  projected_production: string | Decimal;
  rjo_hedged_quantity: string | Decimal;
  has_rjo_hedged_quantity: boolean;

  hta_volume: string | Decimal;
  basis_volume: string | Decimal;
  beacon_volume: string | Decimal;
  beacon_quantity: string | Decimal;
  rjo_percent_hedged: string | Decimal;
  without_beacon_percent_hedged: string | Decimal;
  beacon_percent_hedged: string | Decimal;
  rjo_options: string;
  otc_contracts: Record<string, Decimal | string>;
}

type FiltersState = z.infer<typeof CashSalesFiltersValidation>;

interface SearchFilterState {
  searchTerm: string;
  showFilters: boolean;
}

type TabType = "summary" | "corn" | "soybeans" | "canola" | "wheat";

function getStoredJson(key: string, default_value: any) {
  try {
    return JSON.parse(localStorage.getItem(key) || JSON.stringify(default_value));
  } catch (error) {
    return default_value;
  }
}

export function RiskCashSummaryCommodityLevel({ data, commodities }: { commodities: Commodities[]; data: RiskManagementSummaryResult | undefined; }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailModalInfo, setDetailModalInfo] = useState<null | { customer_id: string; crop_year: number; commodity: string }>(null);
  const [visibleOtcColumns, setVisibleOtcColumns] = useState<OtcContractType[]>(getStoredJson("risk:otc_columns", []));
  const [hideNoProduction, setHideNoProduction] = useState<{ value: boolean }>(getStoredJson("risk:hide_no_production", { value: true }));
  const [selectedPreviewCustomer, setSelectedPreviewCustomerId] = useState<null | { id: string; name: string }>(null);
  const minimalListCustomerResult = useListCustomers({ minimal: true });
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("risk:otc_columns", JSON.stringify(visibleOtcColumns));
    localStorage.setItem("risk:hide_no_production", JSON.stringify(hideNoProduction));
  }, [visibleOtcColumns, hideNoProduction])



  // Process data into rows
  const { all_rows, hasRowValues, columns: filteredColumns } = useMemo(() => {
    if (!data?.summary?.commodity || commodities.every(commodity => !data?.summary?.commodity?.[commodity])) {
      return { all_rows: [], hasRowValues: false, columns: [] };
    }

    let hasRowValues = false;
    const allRows: CommoditySummaryRow[] = [];
    const { commodity: commodityData, commodity_customer_map } = data.summary;

    // Iterate through each commodity
    Object.entries(commodityData).forEach(([commodityId, commoditySummary]) => {
      if (!commodities.includes(commodityId as Commodities)) return;
      // Iterate through each customer in this commodity
      const customerMap = commodity_customer_map[commodityId] || {};
      Object.entries(commoditySummary).forEach(([customerId, customerSummary]) => {
        if (!hasRowValues) { hasRowValues = true; }

        const customerName = customerMap[customerId] || 'Unknown Customer';
        const contractQuantities = customerSummary.general_contract_types_quantities || {};
        const commodityDisplay = CommodityOptions.find(option => option.value === commodityId)?.label || commodityId;

        const zeroDecimal = safeParseDecimal({})(0);
        const projected_production = safeParseDecimal({})(customerSummary.projected_production);
        if (projected_production.isZero() && hideNoProduction.value) {
          return;
        }
        const rjo_options_quantity = safeParseDecimal({})(customerSummary.rjo_options_quantity);
        const rjo_futures_quantity = safeParseDecimal({})(customerSummary.rjo_futures_quantity);
        const beacon_quantity = safeParseDecimal({})(customerSummary.beacon_quantity);

        const rjo_hedged_quantity = rjo_futures_quantity.plus(rjo_options_quantity);
        const has_rjo_hedged_quantity = !rjo_hedged_quantity.isZero();
        const hasProjectedQuantity = !projected_production.isZero();

        allRows.push({
          id: `${commodityId}_:_${customerId}`,
          crop_year: customerSummary.crop_year,
          commodity: commodityDisplay,
          commodityId,
          customer_id: customerId,
          customer_name: customerName,
          acreage: customerSummary.acreage,
          projected_production,
          rjo_hedged_quantity,
          has_rjo_hedged_quantity,
          beacon_quantity,
          rjo_percent_hedged: !hasProjectedQuantity ? zeroDecimal : rjo_hedged_quantity.div(projected_production).times(100),
          without_beacon_percent_hedged: !hasProjectedQuantity ? zeroDecimal : Decimal.max(0, (rjo_hedged_quantity.sub(beacon_quantity)).div(projected_production).times(100)),
          beacon_percent_hedged: !hasProjectedQuantity ? zeroDecimal : (beacon_quantity).div(projected_production).times(100),

          otc_contracts: customerSummary.otc_contract_types_quantities,
          hta_volume: customerSummary.general_contract_types_quantities["HTA"] || zeroDecimal,
          basis_volume: customerSummary.general_contract_types_quantities["Basis"] || zeroDecimal,
          beacon_volume: customerSummary.general_contract_types_quantities["Beacon"] || zeroDecimal,
          rjo_options: customerSummary.rjo_option_types.sort().join(" & ")
        });
      });
    });

    const contractTypeColumns = OtcContractTypeOptions.sort((a, b) => a.label.localeCompare(b.label)).map(option => {
      return {
        key: option.value,
        name: option.label,
        isVisible: visibleOtcColumns.includes(option.value),
        renderCell: ({ row }: any) => {
          const quantity: Decimal = safeParseDecimal({})(row.otc_contracts[option.value]) || new Decimal(0);
          const percent: Decimal = row.projected_production.isZero() ? new Decimal(0) : quantity.div(row.projected_production).times(100);
          return formatNumberToDisplay(percent, { emptyFormat: "--", suffix: "%" });
        }
      }
    });

    const tableColumns: any = [
      {
        key: 'customer_name',
        name: 'Client List',
        isVisible: true,
        cell: ({ row }: any) => (
          <button className="underline decoration-solid decoration-sky-500 cursor-pointer" onClick={() => setSelectedPreviewCustomerId({ id: row.original.customer_id, name: row.original.customer_name })}>
            {row.original.customer_name}
          </button>
        ),
        enableColumnFilter: true,
      },
      {
        key: 'acreage',
        name: 'Acreage',
        isVisible: true,
        renderCell: ({ row }: any) => {
          return <button onClick={(e) => {
            if (e.preventDefault) { e.preventDefault(); }

            const searchParams = new URLSearchParams()
            searchParams.set("customer_id", row.customer_id);
            searchParams.set("crop_year", row.crop_year);
            searchParams.set("commodities", row.commodity);

            navigate({
              pathname: '/risk/management',
              search: searchParams.toString()
            })
          }} className="underline decoration-solid decoration-sky-500 cursor-pointer">{formatNumberToDisplay(row.acreage, { emptyFormat: "--" })}</button>;
        }
      },
      {
        key: 'projected_production',
        name: 'Projected Production',
        isVisible: true,
        renderCell: ({ row }: any) => {
          return <button className="border p-1 border-solid border-sky-500 cursor-pointer" onClick={(e) => {
            if (e.preventDefault) { e.preventDefault(); }
            setDetailModalInfo({
              customer_id: row.customer_id,
              crop_year: row.crop_year,
              commodity: row.commodity,
            })
          }} >{formatNumberToDisplay(row.projected_production, { emptyFormat: "--" })}</button>;
        }
      },
      {
        key: 'rjo_percent_hedged',
        name: 'Percent Hedged',
        isVisible: true,
        renderCell: ({ row }: any) => {
          return formatNumberToDisplay(row.rjo_percent_hedged, { emptyFormat: "--", suffix: "%" });
        }
      },
      {
        key: 'without_beacon_percent_hedged',
        name: 'W/O beacon percent',
        isVisible: true,
        renderCell: ({ row }: any) => {
          return formatNumberToDisplay(row.without_beacon_percent_hedged, { emptyFormat: "--", suffix: "%" });
        }
      },
      {
        key: 'rjo_options',
        name: 'RJO Options',
        isVisible: true,
      },
      {
        key: 'tt_working_orders',
        name: 'TT Working Orders',
        isVisible: false,
      },
      {
        key: 'hta_volume',
        name: 'HTA Vol',
        isVisible: true,
        renderCell: ({ row }: any) => {
          return formatNumberToDisplay(row.hta_volume, { emptyFormat: "--" });
        }
      },
      {
        key: 'basis_volume',
        name: 'Basis Vol',
        isVisible: true,
        renderCell: ({ row }: any) => {
          return formatNumberToDisplay(row.basis_volume, { emptyFormat: "--" });
        }
      },
      {
        key: 'beacon_volume',
        name: 'Beacon Vol',
        isVisible: true,
        renderCell: ({ row }: any) => {
          return formatNumberToDisplay(row.beacon_volume, { emptyFormat: "--" });
        }
      },
      {
        key: 'beacon_percent_hedged',
        name: 'Beacon Percent',
        isVisible: true,
        renderCell: ({ row }: any) => {
          return formatNumberToDisplay(row.beacon_percent_hedged, { emptyFormat: "--", suffix: "%" });
        }
      },
      ...contractTypeColumns
    ].filter(item => item.isVisible).map((item: any) => ({ ...item, enableColumnFilter: item.enableColumnFilter || false, accessorKey: item.accessorKey || item.key, header: item.header || item.name, cell: item.cell || (item.renderCell ? ({ row }: any) => item.renderCell({ row: row.original }) : undefined) }));

    allRows.sort((a: CommoditySummaryRow, b: CommoditySummaryRow) => {
      const commodityCompare = a.commodity.localeCompare(b.commodity);
      if (commodityCompare !== 0) return commodityCompare;
      return a.customer_name.localeCompare(b.customer_name);
    })

    // Sort by commodity, then by customer name
    return {
      all_rows: allRows,
      hasRowValues,
      columns: tableColumns
    };
  }, [data, visibleOtcColumns, hideNoProduction]);


  return (
    <div className="space-y-2">
      <div className="flex gap-4">
        <span className="font-semibold">Filters and Options:</span>

        <ColumnsDialog setVisibleOtcOptions={setVisibleOtcColumns as any} selectedOtcOptions={visibleOtcColumns} />
        <div>
          <Badge id="hideEmptyProduction" className={getSelectedColumnBadgeClassnames({ value: hideNoProduction.value ? "1" : "0", label: "" }, ["1"])} onClick={() => setHideNoProduction({ value: !hideNoProduction.value })}>
            Hide Empty Production
          </Badge>
        </div>
      </div>
      <div className="max-h-[800px]">
        <DataTable
          height="600px"
          columns={filteredColumns}
          data={all_rows!}
          className={``}
        />
      </div>

      {!!detailModalInfo && (
        <WrappedProfileModal
          onClose={() => setDetailModalInfo(null)}
          detailInfo={detailModalInfo}
        />
      )}

      {selectedPreviewCustomer?.id && (
        <RiskCustomerModal
          isOpen={!!selectedPreviewCustomer}
          onClose={() => setSelectedPreviewCustomerId(null)}
          customerId={selectedPreviewCustomer?.id!}
          customerName={selectedPreviewCustomer?.name!}
        />
      )}
    </div>
  );
}

export function ColumnsDialog({ setVisibleOtcOptions, selectedOtcOptions }: { selectedOtcOptions: OtcContractType[]; setVisibleOtcOptions: (item: string[]) => void }) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const options = OtcContractTypeOptions;

  const onClickBadge = (option: { value: OtcContractType }) => () => {
    if (selectedOtcOptions.includes(option.value)) {
      setVisibleOtcOptions(selectedOtcOptions.filter(item => item !== option.value));
    } else {

      setVisibleOtcOptions([...selectedOtcOptions, option.value]);
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(status) => {
        setIsOpen(status);
      }}
    >
      <DialogTrigger>
        <span className="sr-only">Show Table Column Options</span>
        <Columns3 className="h-4 w-4" />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Column Options
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Select Columns to add to the table.</p>
        <p className="text-xs text-muted-foreground">These are local to just your computer, these do not transfer to other users.</p>

        <div className="grid lg:grid-cols-2 gap-4">
          {options.map(option => (
            <Badge key={`cash_column_dialog_${option.value}`} className={getSelectedColumnBadgeClassnames(option, selectedOtcOptions)} onClick={onClickBadge({ value: option.value as OtcContractType })}>
              {option.label}
            </Badge>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getSelectedColumnBadgeClassnames(option: { value: string; label: string }, items: string[]) {
  if (items.includes(option.value)) {
    return "bg-yellow-100 text-yellow-800 hover:text-orange-300";
  }
  return "bg-gray-100 text-gray-800 hover:text-orange-300";
}

function WrappedProfileModal({ detailInfo, onClose }: { detailInfo: { customer_id: string; crop_year: number; commodity: string }; onClose: any }) {
  const customerProfilesResult = useListCustomerProfiles({
    customer_id: detailInfo.customer_id,
    filters: {
      is_enabled: true,
      commodities: [detailInfo.commodity as AcceptedCommodity],
      crop_year: [detailInfo.crop_year],
    }
  });

  const { data: customerProfileData, isLoading: isLoadingCustomerProfiles, error: errorCustomerProfiles } = customerProfilesResult;
  const profiles = customerProfileData?.data?.profiles || [];
  const histories = customerProfileData?.data?.histories || [];

  const selectedProfileForDetail = profiles[0]!;
  const selectedProfileHistoriesForDetail = histories.filter((history: RiskCropProfileHistory) => history.profile_id === selectedProfileForDetail?.id)

  return (
    <RiskProfileDetailModal
      isOpen={true}
      onClose={onClose}
      profile={selectedProfileForDetail}
      histories={selectedProfileHistoriesForDetail}
    />
  )
}
